import { useCallback, useEffect, useMemo, useRef } from 'react';
import { usePreferences, useUpdatePreferences } from './usePreferences';

/**
 * Quick 260726-dcz — Per-project, per-column collapse state for the Kanban
 * swimlane board. Sibling of `useFoldState` (which folds milestone *rows*);
 * this one collapses state *columns*.
 *
 * Reads from the existing `usePreferences` TanStack Query cache (no extra fetch).
 * Writes via `useUpdatePreferences` with a 250ms debounce and a FULL
 * `boardColumnCollapse` object so concurrent project edits don't wipe each other
 * (mirrors useFoldState Pitfall 3).
 *
 * Semantics differ from useFoldState in one key way — we store an EXPLICIT
 * boolean, not the literal `true`:
 *   - `boardColumnCollapse[projectId][columnKey] === true`  → collapsed
 *   - `... === false`                                       → explicitly expanded
 *   - absent                                                → `defaultCollapsed`
 * The default is passed per column (`column.isTerminal`), so terminal/"Done"
 * columns collapse by default while a user's explicit expand still persists.
 * Toggling a column back to its default deletes the key to keep the map minimal.
 *
 * MKS-487 — `hasStoredPreference` exposes that third state (an explicit entry
 * vs. none) without changing what is stored. The hide-empty-columns rule needs
 * it: a column the user has deliberately collapsed or expanded keeps that
 * choice, and only a column with no entry at all is hidden when it runs empty.
 */
export function useColumnCollapse(projectId: string): {
  isCollapsed: (columnKey: string, defaultCollapsed: boolean) => boolean;
  onToggleCollapse: (columnKey: string, defaultCollapsed: boolean) => void;
  hasStoredPreference: (columnKey: string) => boolean;
} {
  const { data: preferences } = usePreferences();
  const updateMutation = useUpdatePreferences();
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Accumulator surviving across debounced calls so rapid multi-column toggles
  // within the 250ms window coalesce into one PATCH (see useFoldState CR-03).
  const pendingRef = useRef<Record<string, boolean> | null>(null);

  const collapses = useMemo<Record<string, boolean>>(
    () => preferences?.boardColumnCollapse?.[projectId] ?? {},
    [preferences, projectId],
  );

  const isCollapsed = useCallback(
    (columnKey: string, defaultCollapsed: boolean) =>
      collapses[columnKey] ?? defaultCollapsed,
    [collapses],
  );

  const hasStoredPreference = useCallback(
    (columnKey: string) => typeof collapses[columnKey] === 'boolean',
    [collapses],
  );

  const onToggleCollapse = useCallback(
    (columnKey: string, defaultCollapsed: boolean) => {
      const base =
        pendingRef.current ?? preferences?.boardColumnCollapse?.[projectId] ?? {};
      const current = base[columnKey] ?? defaultCollapsed;
      const nextVal = !current;
      const next: Record<string, boolean> = { ...base };
      if (nextVal === defaultCollapsed) {
        delete next[columnKey]; // back to default → drop the key
      } else {
        next[columnKey] = nextVal;
      }
      pendingRef.current = next;

      const nextBoardColumnCollapse = {
        ...preferences?.boardColumnCollapse, // preserve other projects' entries
        [projectId]: next,
      };

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        updateMutation.mutate({ boardColumnCollapse: nextBoardColumnCollapse });
        pendingRef.current = null; // accumulator drained on flush
      }, 250);
    },
    [preferences, projectId, updateMutation],
  );

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    [],
  );

  return { isCollapsed, onToggleCollapse, hasStoredPreference };
}
